import React from "react";
import { IconCircleCheckFilled, IconCircle } from "@tabler/icons-react";
import { useLanguage } from "../context/LanguageContext";

interface PasswordStrengthIndicatorProps {
  value: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ value = "" }) => {
  const { t } = useLanguage();

  if (!value || value.length === 0) return null;

  const has8Chars = value.length >= 8;
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  const hasSpecial = /[^A-Za-z0-9]/.test(value);

  const passedCount = [has8Chars, hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;

  let strengthLabel = "Weak";
  let labelColor = "text-red-500";
  let barColor = "bg-red-500";
  let barWidth = "33%";

  if (passedCount >= 5) {
    strengthLabel = "Strong";
    labelColor = "text-green-500";
    barColor = "bg-green-500";
    barWidth = "100%";
  } else if (passedCount >= 3) {
    strengthLabel = "Medium";
    labelColor = "text-amber-500";
    barColor = "bg-amber-500";
    barWidth = "66%";
  }

  const rules = [
    { label: "8 Chars", isMet: has8Chars },
    { label: "A-Z", isMet: hasUpper },
    { label: "a-z", isMet: hasLower },
    { label: "123", isMet: hasNumber },
    { label: "@#$", isMet: hasSpecial },
  ];

  return (
    <div className="w-full mt-2 space-y-1.5 transition-all duration-200">
      {/* Header row: Strength Label */}
      <div className="flex items-center justify-between px-0.5">
        <span className="text-xs font-semibold text-slate-500">
          {t("Password Strength")}
        </span>
        <span className={`text-xs font-bold ${labelColor}`}>
          {t(strengthLabel)}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300 rounded-full`}
          style={{ width: barWidth }}
        />
      </div>

      {/* Validation Rules Indicator List */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 pt-1 px-0.5 text-xs">
        {rules.map((rule) => (
          <div key={rule.label} className="flex items-center gap-1">
            {rule.isMet ? (
              <IconCircleCheckFilled size={15} className="text-green-500 shrink-0" />
            ) : (
              <IconCircle size={15} className="text-slate-400 shrink-0" />
            )}
            <span
              className={
                rule.isMet
                  ? "text-green-600 font-semibold"
                  : "text-slate-500 font-medium"
              }
            >
              {rule.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
